/**
 * Book Appointment — select doctor, date, type and confirm.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { appointmentAPI, patientAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BookAppointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDoctor = searchParams.get('doctor') || '';

  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [doctorInfo, setDoctorInfo] = useState(null); // schedule + available_days
  const [form, setForm] = useState({
    doctor_uid: preselectedDoctor,
    appointment_date: '',
    appointment_type: 'regular',
    symptoms: '',
    reason: '',
  });

  // Load doctor detail (schedule/availability) whenever a doctor is chosen
  const loadDoctorDetail = useCallback(async (uid) => {
    if (!uid) { setDoctorInfo(null); return; }
    try {
      const res = await patientAPI.getDoctorDetail(uid);
      setDoctorInfo(res.data || null);
    } catch {
      setDoctorInfo(null);
    }
  }, []);

  useEffect(() => {
    if (preselectedDoctor) {
      loadDoctorDetail(preselectedDoctor);
    } else {
      // Load doctors list for dropdown selection
      const loadDoctors = async () => {
        try {
          const res = await patientAPI.searchDoctors({ specialization: '' });
          setDoctors(Array.isArray(res.data) ? res.data : (res.data?.doctors || []));
        } catch { /* no-op */ }
      };
      loadDoctors();
    }
  }, [preselectedDoctor, loadDoctorDetail]);

  // Check if the selected date falls on an unavailable day
  const selectedDayName = form.appointment_date ? DAY_NAMES[new Date(form.appointment_date + 'T12:00:00').getDay()] : null;
  const isDayUnavailable = doctorInfo?.available_days?.length > 0
    && selectedDayName
    && !doctorInfo.available_days.includes(selectedDayName);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctor_uid || !form.appointment_date) {
      toast.error('Please select a doctor and date.');
      return;
    }
    if (isDayUnavailable) {
      toast.error(`Doctor is not available on ${selectedDayName}s. Available: ${doctorInfo.available_days.join(', ')}.`);
      return;
    }
    setLoading(true);
    try {
      // Pass the date at noon so the backend can compute the actual slot time
      const appointmentDatetime = new Date(`${form.appointment_date}T12:00:00`).toISOString();
      const payload = {
        ...form,
        appointment_date: appointmentDatetime,
        reason: form.reason || form.symptoms || 'Consultation',
      };
      const res = await appointmentAPI.book(payload);
      const appointmentId = res.data?.appointment_id;
      const fee = doctorInfo?.consultation_fee || 500;
      toast.success(`Appointment booked! Queue position: #${res.data?.queue_position || 1}. Please complete payment.`);
      // Redirect to payment immediately so the appointment appears in the doctor's queue
      navigate(`/patient/payment?appointment=${appointmentId}&amount=${fee}`);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : (err?.error || err?.message || 'Failed to book appointment.'));
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Layout title="Book Appointment" role="patient" showBack>
      <div className="p-4 pb-24">
        <form onSubmit={handleSubmit} className="card space-y-4">
          {!preselectedDoctor && (
            <div>
              <label className="label">Select Doctor *</label>
              <select
                value={form.doctor_uid}
                onChange={e => {
                  setForm(f => ({ ...f, doctor_uid: e.target.value }));
                  loadDoctorDetail(e.target.value);
                }}
                className="input-field"
                required
              >
                <option value="">Choose a Doctor</option>
                {doctors.map(d => (
                  <option key={d.uid || d.id} value={d.uid || d.id}>
                    Dr. {d.full_name} — {d.specialization} (KES {d.consultation_fee || 500})
                  </option>
                ))}
              </select>
            </div>
          )}

          {preselectedDoctor && (
            <div className="bg-primary-50 rounded-xl p-3 text-sm">
              <p className="text-primary-700 font-medium">Doctor has been pre-selected from your search.</p>
              <button
                type="button"
                onClick={() => navigate(`/patient/doctors/${preselectedDoctor}`)}
                className="mt-2 text-xs font-semibold text-primary-700 underline"
              >
                View doctor profile
              </button>
            </div>
          )}

          {doctorInfo?.uid && (
            <div className="bg-white border border-primary-100 rounded-xl p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900">Dr. {doctorInfo.full_name || 'Selected Doctor'}</p>
                  <p className="text-primary-600">{doctorInfo.specialization || 'General Practice'}</p>
                  {doctorInfo.hospital_affiliation && (
                    <p className="text-xs text-gray-500 mt-1">🏥 {doctorInfo.hospital_affiliation}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/patient/doctors/${doctorInfo.uid}`)}
                  className="px-3 py-1.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg hover:bg-primary-100 transition-colors"
                >
                  Full Profile
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="label">Appointment Date *</label>
            <input
              type="date"
              value={form.appointment_date}
              onChange={e => setForm(f => ({ ...f, appointment_date: e.target.value }))}
              className={`input-field ${isDayUnavailable ? 'border-red-400 focus:ring-red-400' : ''}`}
              min={today}
              required
            />
            {/* Show available days for this doctor */}
            {doctorInfo?.available_days?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => (
                  <span key={day} className={`text-xs px-2 py-0.5 rounded-full ${
                    doctorInfo.available_days.includes(day)
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-400 line-through'
                  }`}>{day.slice(0,3)}</span>
                ))}
              </div>
            )}
            {isDayUnavailable && (
              <p className="mt-1 text-xs text-red-600">
                ⚠️ Doctor is not available on {selectedDayName}s.
                Available days: {doctorInfo.available_days.join(', ')}.
              </p>
            )}
          </div>

          <div>
            <label className="label">Appointment Type</label>
            <select
              value={form.appointment_type}
              onChange={e => setForm(f => ({ ...f, appointment_type: e.target.value }))}
              className="input-field"
            >
              <option value="regular">Regular Consultation</option>
              <option value="follow_up">Follow-Up Visit</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>

          <div>
            <label className="label">Reason for Visit *</label>
            <input
              type="text"
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value, symptoms: e.target.value }))}
              className="input-field"
              placeholder="Brief reason for visit (e.g. diabetes check-up)"
              required
            />
          </div>

          <div>
            <label className="label">Describe Your Symptoms</label>
            <textarea
              value={form.symptoms}
              onChange={e => setForm(f => ({ ...f, symptoms: e.target.value, reason: form.reason || e.target.value }))}
              className="input-field"
              rows="3"
              placeholder="Describe your symptoms in detail..."
            />
          </div>

          {/* Queue info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
            <p className="font-medium">Smart Queue System</p>
            <p className="text-blue-600 mt-1">Your queue position will be assigned based on appointment type. Emergency cases get highest priority. Estimated wait times aim for a 30% reduction.</p>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            {loading && <div className="spinner w-5 h-5 border-2"></div>}
            Confirm Booking
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default BookAppointment;
